<template>
  <div class="proposal-staging">
    <v-slide-x-transition mode="out-in">
      <LoadingListPlaceholder absolute v-if="loading" />
    </v-slide-x-transition>
    <slot />
    <v-slide-x-transition mode="out-in">
      <div v-if="!loading">
        <v-slide-x-transition mode="out-in">
          <div
            v-if="stage == 'before' && !$store.getters.isViewer"
            :style="{ paddingTop: '8em' }"
            class="upper-layer d-flex align-content-center flex-column text-center"
          >
            <v-icon size="80" color="accent"
              >mdi-file-document-edit-outline</v-icon
            >
            <v-btn
              outlined
              color="accent"
              class="mt-6"
              @click="switchToEditMode"
              >Make edit request</v-btn
            >
          </div>
          <div
            v-else-if="stage == 'before' && $store.getters.isViewer"
            :style="{ paddingTop: '8em' }"
            class="upper-layer d-flex align-content-center flex-column text-center"
          >
            <v-icon size="80" color="accent">mdi-eye</v-icon>
            <v-btn
              outlined
              color="accent"
              class="mt-6"
              @click="switchToEditMode"
              >View edit request</v-btn
            >
          </div>
          <div
            v-else-if="stage == 'pending'"
            :style="{ paddingTop: '8em' }"
            class="upper-layer d-flex align-content-center flex-column text-center"
          >
            <v-icon size="80" color="accent"
              >mdi-clipboard-clock-outline</v-icon
            >
            <div class="text-h5 mt-2 font-weight-light">
              <span v-if="lastProposal.status === STATUS_LIST.CREATE_REQUEST"
                >Pending create request</span
              >
              <span v-else-if="lastProposal.status === STATUS_LIST.EDIT_REQUEST"
                >Pending edit request</span
              >
              <span
                v-else-if="
                  lastProposal.status === STATUS_LIST.DISCONTINUATE_REQUEST
                "
                >Pending discontinuate request</span
              >
              <span
                v-else-if="lastProposal.status === STATUS_LIST.DELETE_REQUEST"
                >Pending delete request</span
              >
              <span v-else>
                No pending request
              </span>
            </div>
            <div class="subtitle-1 mt-2 font-weight-light grey--text">
              <span v-if="!lastProposal.status">
                <!-- No message if no pending request -->
              </span>
              <span v-else-if="$store.getters.isUserAdmin">
                As an admin, you are required to verify, approve or reject this
                request
              </span>
              <span v-else>
                Our team is currently verifying your request
              </span>
            </div>

            <div>
              <template v-if="$store.getters.isUserAdmin">
                <v-btn
                  text
                  color="primary"
                  @click="switchToEditMode"
                  class="mt-5"
                >
                  <v-icon left>mdi-eye</v-icon> verify request
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  @click="$emit('cancel')"
                  color="red"
                  dark
                  class="mt-4 mr-3"
                  text
                >
                  <v-icon left>mdi-close</v-icon>
                  Cancel</v-btn
                >
                <v-btn
                  @click="switchToEditMode"
                  color="blue"
                  dark
                  class="mt-4"
                  v-if="isUpdatable"
                >
                  <v-icon left>mdi-pen</v-icon>
                  Update</v-btn
                >
              </template>
            </div>
          </div>
          <div
            v-else-if="stage === 'verified' && lastProposal !== null"
            :style="{ paddingTop: '8em' }"
            class="upper-layer d-flex align-content-center flex-column text-center"
          >
            <v-icon
              size="80"
              :color="lastProposal.approved ? 'success' : 'error'"
              >mdi-{{
                lastProposal.approved
                  ? "clipboard-check-outline"
                  : "clipboard-remove-outline"
              }}</v-icon
            >
            <div class="text-h5 mt-2 font-weight-light">
              Request {{ lastProposal.approved ? "approved" : "rejected" }}
            </div>
            <div
              class="subtitle-1 mt-2 font-weight-light grey--text"
              style="max-width:70%"
            >
              The "{{ lastProposal.status.replace("_REQUEST", "") }}" request
              made the
              {{
                moment(lastProposal.created_at).format("DD/MM/YYYY [at] HH:mm")
              }}
              has been verified and
              {{ lastProposal.approved ? "approved" : "rejected" }} by our
              moderators.
            </div>

            <v-dialog
              v-model="rejectionMessageDialog"
              v-if="
                !lastProposal.approved && lastProposal.verified_attached_message
              "
              :width="$vuetify.breakpoint.mdAndUp ? '25%' : '100%'"
            >
              <template v-slot:activator="{ on }">
                <v-btn color="primary" text class="my-2 mb-0" v-on="on">
                  <v-icon left>mdi-comment-eye</v-icon>
                  Read rejection message
                </v-btn>
              </template>
              <v-card>
                <v-card-title>Rejection message</v-card-title>
                <v-card-text class="pt-2">
                  {{ lastProposal.verified_attached_message }}
                </v-card-text>
                <v-card-actions class="pb-3 pt-0 justify-end">
                  <v-btn
                    text
                    class="mx-1"
                    @click="rejectionMessageDialog = false"
                    >Close</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-btn
              outlined
              color="accent"
              class="mt-6"
              @click="isEditing = true"
              >Make new request</v-btn
            >
          </div>
        </v-slide-x-transition>
      </div>
    </v-slide-x-transition>
  </div>
</template>

<script>
import LoadingListPlaceholder from "@/components/base/LoadingListPlaceholder"
import { PROPOSAL_STATUS } from "@/data"
import moment from "moment"
export default {
  components: {
    LoadingListPlaceholder
  },
  props: {
    cancelEditCounter: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    },
    validationStage: {
      type: Boolean,
      default: false
    },
    lastProposal: {
      type: Object,
      default: null
    }
  },
  watch: {
    cancelEditCounter(newValue, oldValue) {
      this.isEditing = false
      this.$emit("edit", false)
    },
    lastProposal: {
      deep: true,
      handler(newValue, oldValue) {
        if (
          (newValue !== null && oldValue == null) ||
          newValue.status !== oldValue.status
        )
          this.isEditing = false
        this.$emit("edit", false)
      }
    }
  },
  computed: {
    isUpdatable() {
      return [
        this.STATUS_LIST.EDIT_REQUEST,
        this.STATUS_LIST.CREATE_REQUEST
      ].includes(this.lastProposal.status)
    },
    stage() {
      // before -> editing -> pending -> verified
      let stage = "before"
      if (this.isEditing || this.validationStage) {
        stage = "editing"
      } else if (this.lastProposal.pending_request != null) {
        if (this.lastProposal.verified_at) {
          stage = "verified"
        } else {
          stage = "pending"
        }
        // Allow admins to modify values directly if there is no pending request
      } else if (this.$store.state.user.admin) {
        stage = "editing"
      }
      return stage
    }
  },
  methods: {
    moment,
    switchToEditMode() {
      this.isEditing = true
      this.$emit("edit", true)
    }
  },
  data() {
    return {
      rejectionMessageDialog: false,
      STATUS_LIST: PROPOSAL_STATUS,
      isEditing: false
    }
  }
}
</script>

<style lang="scss" scoped>
.proposal-staging {
  position: relative;
  .upper-layer {
    align-items: center;
    background-color: rgba(255, 255, 255, 0.9);
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
  }
}
</style>
